import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  RemoveCircleOutline as RemoveLineIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, BankAccountPicker, CurrencyPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import {
  CashStatusChip,
  StatStrip,
  fmtDate,
  getErrorMessage,
  unwrap,
} from './CashShared';

const DIRECTIONS = ['RECEIPT', 'PAYMENT'];
const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const bankLabel = (v, look) => {
  if (v?.bankAccount) return `${v.bankAccount.account_code} - ${v.bankAccount.account_name}`;
  return look.accountLabel(v?.bank_account_id);
};

const lineLabel = (l, look) => {
  if (l?.account) return `${l.account.account_code} - ${l.account.account_name}`;
  return look.accountLabel(l?.account_id);
};

const emptyLine = () => ({ account_id: '', amount: '', description: '' });
const emptyForm = () => ({
  direction: 'RECEIPT', bank_account_id: '', voucher_date: new Date().toISOString().slice(0, 10),
  currency_id: '', description: '', reference: '', lines: [emptyLine(), emptyLine()],
});

// Standalone over-the-counter receipts/payments (sundry income, direct
// expenses) - not tied to AP/AR. DRAFT -> APPROVED -> POSTED; posted voids
// reverse the journal.
const VouchersTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.CASH, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.CASH, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.CASH, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const setLine = (index, key, value) => {
    setValues((p) => ({ ...p, lines: p.lines.map((l, i) => (i === index ? { ...l, [key]: value } : l)) }));
  };
  const addLine = () => setValues((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  const removeLine = (index) => setValues((p) => ({ ...p, lines: p.lines.filter((_, i) => i !== index) }));

  const linesTotal = values.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const submitCreate = async () => {
    if (!values.bank_account_id) {
      showSnackbar('Bank/cash account is required', 'error');
      return;
    }
    if ((values.description?.trim() ?? '').length < 3) {
      showSnackbar('Description (min 3 chars) is required', 'error');
      return;
    }
    const lines = values.lines
      .filter((l) => l.account_id || l.amount)
      .map((l) => ({
        account_id: Number(l.account_id),
        amount: Number(l.amount),
        ...(l.description?.trim() ? { description: l.description.trim() } : {}),
      }));
    if (lines.length < 1) {
      showSnackbar('At least one counter line is required', 'error');
      return;
    }
    if (lines.some((l) => !l.account_id || !(l.amount > 0))) {
      showSnackbar('Each line needs an account and an amount greater than zero', 'error');
      return;
    }
    const payload = {
      direction: values.direction,
      bank_account_id: Number(values.bank_account_id),
      voucher_date: values.voucher_date,
      description: values.description.trim(),
      lines,
    };
    if (values.currency_id) payload.currency_id = Number(values.currency_id);
    if (values.reference?.trim()) payload.reference = values.reference.trim();
    try {
      setSaving(true);
      await ledgerAPI.createCashVoucher(payload);
      showSnackbar('Voucher created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn, after) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this voucher?`,
      onConfirm: async () => {
        try {
          const res = await fn();
          showSnackbar(`Voucher ${pastTense} successfully`, 'success');
          if (after) after(res);
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getCashVoucher(row.voucher_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch voucher'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidCashVoucher(voidDlg.item.voucher_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Voucher voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailLines = Array.isArray(detail?.lines) ? detail.lines : [];
  const detailTotal = detailLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.CASH}
        title="Cash Vouchers"
        subtitle="Over-the-counter receipts and direct payments outside AP/AR"
        addLabel="New Voucher"
        idKey="voucher_id"
        searchPlaceholder="Search voucher #, description..."
        emptyMessage="No cash vouchers found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getCashVouchers(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'direction', label: 'Direction', options: DIRECTIONS.map((d) => ({ label: d, value: d })) },
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
        ]}
        columns={[
          { label: 'Voucher #', render: (v) => <strong>{v.voucher_number}</strong> },
          { label: 'Direction', render: (v) => <CashStatusChip status={v.direction} /> },
          { label: 'Bank/Cash', render: (v) => bankLabel(v, look) },
          { label: 'Date', render: (v) => fmtDate(v.voucher_date) },
          { label: 'Total', render: (v) => formatAccountingAmount(v.total_amount), align: 'right' },
          { label: 'Status', render: (v) => <CashStatusChip status={v.status} /> },
        ]}
        rowActions={(v) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(v) }];
          if (v.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approveCashVoucher(v.voucher_id)) });
          }
          if (v.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postCashVoucher(v.voucher_id)) });
          }
          if ((v.status === 'DRAFT' || v.status === 'APPROVED' || v.status === 'POSTED') && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: v, void_reason: '' }) });
          }
          if (v.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete voucher',
                message: `Delete DRAFT voucher ${v.voucher_number}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteCashVoucher(v.voucher_id);
                    showSnackbar('Voucher deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Cash Voucher</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <Autocomplete
                size="small"
                options={DIRECTIONS.map((d) => ({ label: d, value: d }))}
                getOptionLabel={(o) => o.label}
                value={DIRECTIONS.map((d) => ({ label: d, value: d })).find((o) => o.value === values.direction)}
                onChange={(_, o) => setValues((p) => ({ ...p, direction: o?.value || 'RECEIPT' }))}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                renderInput={(params) => <TextField {...params} label="Direction" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <BankAccountPicker label="Bank/Cash Account" value={values.bank_account_id} onChange={(id) => setValues((p) => ({ ...p, bank_account_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Voucher Date" value={values.voucher_date}
                onChange={(e) => setValues((p) => ({ ...p, voucher_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <CurrencyPicker label="Currency (optional)" value={values.currency_id} onChange={(id) => setValues((p) => ({ ...p, currency_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" label="Description (min 3 chars)" value={values.description}
                onChange={(e) => setValues((p) => ({ ...p, description: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" label="Reference" value={values.reference}
                onChange={(e) => setValues((p) => ({ ...p, reference: e.target.value }))}
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" gutterBottom>Counter lines</Typography>
          {values.lines.map((l, i) => (
            <Stack key={i} direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
              <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                <AccountPicker label={`Line ${i + 1} account`} value={l.account_id} onChange={(id) => setLine(i, 'account_id', id)} />
              </Box>
              <TextField
                size="small" type="number" label="Amount" value={l.amount}
                onChange={(e) => setLine(i, 'amount', e.target.value)}
                inputProps={{ min: 0.01, step: 'any' }} sx={{ width: 140 }}
              />
              <TextField
                size="small" label="Narration" value={l.description}
                onChange={(e) => setLine(i, 'description', e.target.value)}
                sx={{ flex: '1 1 180px' }}
              />
              <IconButton size="small" color="error" title="Remove line" onClick={() => removeLine(i)} disabled={values.lines.length <= 1}>
                <RemoveLineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
          <Typography variant="body2" sx={{ mt: 1 }} fontWeight={700}>
            Total: {formatAccountingAmount(linesTotal)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Voucher {detail?.voucher_number}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {bankLabel(detail, look)} - {fmtDate(detail.voucher_date)} - {detail.description}
                {detail.reference ? ` (${detail.reference})` : ''}
              </Typography>
              <StatStrip
                tiles={[
                  { label: 'Total', value: formatAccountingAmount(detailTotal) },
                  { label: 'Direction', value: detail.direction },
                  { label: 'Status', value: <CashStatusChip status={detail.status} /> },
                ]}
              />
              <DataTable
                columns={['Counter Account', 'Narration', 'Amount']}
                data={detailLines}
                loading={false}
                emptyMessage="No lines."
                renderRow={(l, i) => (
                  <TableRow key={l.line_id ?? i}>
                    <TableCell>{lineLabel(l, look)}</TableCell>
                    <TableCell>{l.description || '-'}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(l.amount)}</TableCell>
                  </TableRow>
                )}
              />
              <Typography variant="body2" align="right" sx={{ mt: 1 }} fontWeight={700}>
                Total: {formatAccountingAmount(detailTotal)}
              </Typography>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Voided: {detail.void_reason}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void voucher ${voidDlg.item?.voucher_number || ''}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default VouchersTab;
